import { cn } from "@/utils/utils";
import type { StudentListType } from "@/types/student.model";
import { Check, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

const TYPE_OPTIONS = [
  { value: "course" as const, label: "Course" },
  { value: "class" as const, label: "Class" },
];

function selectionToApiType(selected: Set<"course" | "class">): StudentListType {
  const hasCourse = selected.has("course");
  const hasClass = selected.has("class");
  if (hasCourse && hasClass) return "all";
  if (hasCourse) return "course";
  if (hasClass) return "class";
  return "all";
}

function apiTypeToSelection(type: StudentListType): Set<"course" | "class"> {
  if (type === "course") return new Set(["course"]);
  if (type === "class") return new Set(["class"]);
  return new Set(["course", "class"]);
}

function apiTypeToLabel(type: StudentListType): string {
  if (type === "course") return "Course";
  if (type === "class") return "Class";
  return "Course, Class";
}

interface StudentTypeSelectProps {
  value: StudentListType;
  onChange: (type: StudentListType) => void;
  className?: string;
}

export default function StudentTypeSelect({
  value,
  onChange,
  className = "",
}: StudentTypeSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(() => apiTypeToSelection(value));
  const rootRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    setSelected(apiTypeToSelection(value));
  }, [value]);

  useEffect(() => {
    if (!isOpen || !rootRef.current) return;

    const rect = rootRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: "fixed",
      left: Math.round(rect.left),
      top: Math.round(rect.bottom + 4),
      width: Math.round(rect.width),
      zIndex: 9999,
    });
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        rootRef.current &&
        !rootRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const toggleOption = (option: "course" | "class") => {
    const next = new Set(selected);
    if (next.has(option)) {
      next.delete(option);
    } else {
      next.add(option);
    }
    if (next.size === 0) {
      next.add("course");
      next.add("class");
    }
    setSelected(next);
    onChange(selectionToApiType(next));
  };

  return (
    <div ref={rootRef} className={cn("relative w-full", className)}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="flex h-[48px] w-full items-center justify-between rounded-[12px] border border-[#E5E7EB] bg-white px-4 text-left text-[14px] font-medium text-[#1F1F1F]"
      >
        <span className="text-[#1F1F1F]">{apiTypeToLabel(value)}</span>
        <ChevronDown
          className={cn(
            "h-5 w-5 text-[#1F1F1F] transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {isOpen &&
        typeof window !== "undefined" &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="rounded-[12px] border border-[#E5E7EB] bg-white py-2 shadow-lg"
          >
            {TYPE_OPTIONS.map((option) => {
              const checked = selected.has(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] font-medium text-[#1F1F1F] hover:bg-[#F9FAFB]"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => toggleOption(option.value)}
                >
                  <span
                    className={cn(
                      "flex h-5 w-5 shrink-0 items-center justify-center rounded-[4px] border",
                      checked
                        ? "border-[#5C88C5] bg-[#5C88C5]"
                        : "border-[#D1D5DB] bg-white"
                    )}
                  >
                    {checked ? (
                      <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                    ) : null}
                  </span>
                  {option.label}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}
